import React, { useEffect, useState } from 'react';

import {
  ButtonSwitch, ButtonSwitchGroup, GeoLocation, GeoLocationMarker, Tooltip,
} from '@vlabs/uikit';
import { useDynamicCoordinates } from '@vlabs/uikit/components/geo-location/useDynamicCoordinates';
import { useTranslation } from 'react-i18next';

import type { Location } from '@vlabs/api/luna-api/model';
import { usePreviewLiveProxy } from '@vlabs/api/luna-streams-api/client';

import st from './BaseMap.module.sass';
import type { MapMarker } from './types';

interface BaseMapProps {
  location?: Location | null;
  markers?: MapMarker[];
  streamId?: string | null;
}

type ActiveTab = 'map' | 'stream' | null;

export function BaseMap({
  location = null,
  markers = [],
  streamId = null,
}: BaseMapProps): React.JSX.Element {
  const { t } = useTranslation();
  const { data: streamPreviewUrl } = usePreviewLiveProxy<string>(streamId ?? '', undefined, {
    query: { enabled: !!streamId },
  });
  const [activeTab, setActiveTab] = useState<ActiveTab>(null);

  const latitude = location?.geo_position?.latitude;
  const longitude = location?.geo_position?.longitude;
  const hasCoordinates = latitude != null && longitude != null;
  const position = useDynamicCoordinates({
    latitude: latitude ?? 0,
    longitude: longitude ?? 0,
  });

  useEffect(() => {
    if (hasCoordinates) {
      setActiveTab('map');
      return;
    }

    setActiveTab(streamPreviewUrl ? 'stream' : null);
  }, [hasCoordinates, latitude, longitude, streamPreviewUrl]);

  if (!activeTab) {
    return (
      <div
        className={st.NoData}
        data-testid="workspace.mapStream.emptyContent"
      >
        {t('lunapoint:панель оператора.no data')}
      </div>
    );
  }

  return (
    <div className={st.Root}>
      <ButtonSwitchGroup<'map' | 'stream'>
        className={st.Tabs}
        data-testid="workspace.mapStream.tabsSwitcher"
        onChange={(value) => setActiveTab(value)}
        size="small"
        value={activeTab}
        variant="square"
      >
        <ButtonSwitch
          disabled={!hasCoordinates}
          value="map"
        >
          <Tooltip
            tooltipContent={!hasCoordinates ? t('lunapoint:map stream.no map') : undefined}
            tooltipProps={{ place: 'bottom', offset: 14 }}
          >
            {t('lunapoint:map stream.toggle buttons.map')}
          </Tooltip>
        </ButtonSwitch>

        <ButtonSwitch
          disabled={!streamPreviewUrl}
          value="stream"
        >
          <Tooltip
            tooltipContent={!streamPreviewUrl
              ? t('lunapoint:map stream.no realtime video')
              : undefined}
            tooltipProps={{ place: 'bottom', offset: 14 }}
          >
            {t('lunapoint:map stream.toggle buttons.stream')}
          </Tooltip>
        </ButtonSwitch>
      </ButtonSwitchGroup>

      <div
        className={st.Content}
        data-testid="workspace.mapStream.content"
      >
        {activeTab === 'map' && !!hasCoordinates && (
          <GeoLocation
            center={position}
            toggleFullScreenMode={false}
          >
            {markers.map((marker) => (
              <GeoLocationMarker
                anchor={marker.position}
                className={st.Marker}
                key={marker.id}
              />
            ))}
          </GeoLocation>
        )}

        {activeTab === 'stream' && (
          <img
            alt="Realtime video"
            className={st.Stream}
            src={streamPreviewUrl}
          />
        )}
      </div>
    </div>
  );
}
